
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./PalaceBookings.css";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
).replace(/\/$/, "");

const TABS = [
  { id: "upcoming", label: "Upcoming Bookings", icon: "◷" },
  { id: "pending", label: "Pending", icon: "◷" },
  { id: "confirmed", label: "Confirmed", icon: "✓" },
  { id: "all", label: "All Bookings", icon: "▤" },
];

const normalise = (value) =>
  String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const getStatus = (booking) =>
  normalise(booking.bookingStatus || booking.status || "pending");

const getId = (booking) => booking._id || booking.id;

const getCustomer = (booking) =>
  booking.customerName ||
  booking.guest?.name ||
  booking.customer?.name ||
  booking.guestName ||
  "Guest";

const getPalaceName = (booking) =>
  booking.palace?.propertyName ||
  booking.palace?.name ||
  booking.palaceName ||
  "Palace";

const getRoomName = (booking) =>
  booking.roomCategory?.name ||
  booking.roomCategoryName ||
  booking.roomName ||
  "Room";

const formatDate = (date) => {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const isUpcoming = (booking) => {
  const checkIn = booking.checkIn || booking.checkInDate;
  if (!checkIn) return false;

  const date = new Date(checkIn);
  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  const status = getStatus(booking);
  return (
    date >= today &&
    !["cancelled", "canceled", "rejected", "completed"].includes(status)
  );
};

const getErrorMessage = (data, fallback) =>
  data?.message || data?.error || fallback;

export default function PalaceBookings() {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [vendorNote, setVendorNote] = useState("");

  const fetchBookings = useCallback(async (showLoader = true) => {
    const token = localStorage.getItem("vendorToken");

    if (!token) {
      setError("Vendor login token nahi mila. Please login again.");
      setLoading(false);
      return;
    }

    if (showLoader) setLoading(true);
    else setRefreshing(true);

    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/palace-bookings/vendor`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(
          getErrorMessage(data, "Bookings load nahi ho sakin.")
        );
      }

      const rows = Array.isArray(data)
        ? data
        : data.bookings || data.data?.bookings || data.data || [];

      if (!Array.isArray(rows)) {
        throw new Error("API se bookings ka expected list format nahi mila.");
      }

      setBookings(rows);
    } catch (err) {
      setError(err.message || "Bookings load karte waqt error aaya.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const counts = useMemo(
    () => ({
      upcoming: bookings.filter(isUpcoming).length,
      pending: bookings.filter((b) => getStatus(b) === "pending").length,
      confirmed: bookings.filter((b) =>
        ["confirmed", "approved"].includes(getStatus(b))
      ).length,
      all: bookings.length,
    }),
    [bookings]
  );

  const visibleBookings = useMemo(() => {
    let result = bookings;

    if (activeTab === "upcoming") {
      result = result.filter(isUpcoming);
    } else if (activeTab === "pending") {
      result = result.filter((b) => getStatus(b) === "pending");
    } else if (activeTab === "confirmed") {
      result = result.filter((b) =>
        ["confirmed", "approved"].includes(getStatus(b))
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((b) => getStatus(b) === statusFilter);
    }

    const query = search.trim().toLowerCase();

    if (query) {
      result = result.filter((b) => {
        const searchable = [
          getId(b),
          b.bookingReference,
          getCustomer(b),
          b.customerEmail,
          b.guest?.email,
          b.customer?.email,
          b.customerPhone,
          b.guest?.phone,
          b.customer?.phone,
          getPalaceName(b),
          getRoomName(b),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchable.includes(query);
      });
    }

    return [...result].sort(
      (a, b) =>
        new Date(a.checkIn || a.checkInDate || 0) -
        new Date(b.checkIn || b.checkInDate || 0)
    );
  }, [bookings, activeTab, search, statusFilter]);

  const updateBookingStatus = async (booking, nextStatus) => {
    const bookingId = getId(booking);
    if (!bookingId) {
      setError("Booking ID nahi mila.");
      return;
    }

    const actionLabel =
      nextStatus === "confirmed" ? "confirm" : "cancel";

    if (
      !window.confirm(
        `Kya aap is booking ko ${actionLabel} karna chahte ho?`
      )
    ) {
      return;
    }

    setActionId(bookingId);
    setError("");

    try {
      const token = localStorage.getItem("vendorToken");
      const response = await fetch(
        `${API_BASE}/api/palace-bookings/vendor/${bookingId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ bookingStatus: nextStatus }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(
          getErrorMessage(data, "Booking status update nahi hua.")
        );
      }

      await fetchBookings(false);
      setSelectedBooking(null);
    } catch (err) {
      setError(err.message || "Booking update nahi ho saki.");
    } finally {
      setActionId("");
    }
  };

  const saveNote = async () => {
    if (!selectedBooking) return;

    const bookingId = getId(selectedBooking);
    setActionId(bookingId);
    setError("");

    try {
      const token = localStorage.getItem("vendorToken");
      const response = await fetch(
        `${API_BASE}/api/palace-bookings/vendor/${bookingId}/note`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ vendorNote }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(getErrorMessage(data, "Note save nahi hua."));
      }

      await fetchBookings(false);
      setSelectedBooking(null);
    } catch (err) {
      setError(err.message || "Note save nahi ho saka.");
    } finally {
      setActionId("");
    }
  };

  const openDetails = (booking) => {
    setSelectedBooking(booking);
    setVendorNote(booking.vendorNote || "");
  };

  return (
    <div className="palace-bookings">
      <div className="pb-page-heading">
        <div>
          <div className="pb-eyebrow">PALACE MANAGEMENT</div>
          <h1>Palace Bookings</h1>
          <p>Manage reservations, guests and booking requests.</p>
        </div>

        <button
          type="button"
          className="pb-refresh-btn"
          onClick={() => fetchBookings(false)}
          disabled={refreshing || loading}
        >
          <span className={refreshing ? "pb-spin" : ""}>↻</span>
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="pb-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")}>
            ×
          </button>
        </div>
      )}

      <div className="pb-stats">
        {TABS.map((tab) => (
          <button
            type="button"
            key={tab.id}
            className={`pb-stat-card ${
              activeTab === tab.id ? "pb-stat-active" : ""
            }`}
            onClick={() => {
              setActiveTab(tab.id);
              setStatusFilter("all");
            }}
          >
            <span className="pb-stat-icon">{tab.icon}</span>
            <span className="pb-stat-label">{tab.label}</span>
            <strong>{counts[tab.id]}</strong>
            <span className="pb-stat-bottom">View bookings ↗</span>
          </button>
        ))}
      </div>

      <section className="pb-content">
        <div className="pb-tabs">
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setStatusFilter("all");
              }}
              className={activeTab === tab.id ? "pb-tab-active" : ""}
            >
              {tab.label}
              <span>{counts[tab.id]}</span>
            </button>
          ))}
        </div>

        <div className="pb-toolbar">
          <div className="pb-search">
            <span>⌕</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guest, booking ID, phone..."
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by booking status"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="pb-list-heading">
          <div>
            <h2>{TABS.find((t) => t.id === activeTab)?.label}</h2>
            <p>
              {visibleBookings.length} booking
              {visibleBookings.length === 1 ? "" : "s"} found
            </p>
          </div>
        </div>

        {loading ? (
          <div className="pb-state">
            <div className="pb-loader" />
            <p>Loading Palace bookings...</p>
          </div>
        ) : visibleBookings.length === 0 ? (
          <div className="pb-state pb-empty">
            <div className="pb-empty-icon">▤</div>
            <h3>No bookings found</h3>
            <p>
              {search
                ? "Search change karke dobara try karo."
                : "Is section mein abhi koi booking nahi hai."}
            </p>
            {(search || statusFilter !== "all") && (
              <button
                type="button"
                className="pb-primary-btn"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="pb-booking-list">
            {visibleBookings.map((booking) => {
              const id = getId(booking);
              const status = getStatus(booking);
              const isWorking = actionId === id;
              const canConfirm = ["pending"].includes(status);
              const canCancel = ![
                "cancelled",
                "canceled",
                "completed",
                "rejected",
              ].includes(status);

              return (
                <article className="pb-booking-card" key={id}>
                  <div className="pb-booking-top">
                    <div className="pb-booking-reference">
                      <div className="pb-booking-symbol">♛</div>
                      <div>
                        <span className="pb-muted">BOOKING ID</span>
                        <h3>
                          {booking.bookingReference ||
                            (id ? `#${String(id).slice(-8).toUpperCase()}` : "—")}
                        </h3>
                      </div>
                    </div>

                    <span className={`pb-status pb-status-${status}`}>
                      <i />
                      {status.replace(/_/g, " ")}
                    </span>
                  </div>

                  <div className="pb-property-block">
                    <h3>{getPalaceName(booking)}</h3>
                    <p>{getRoomName(booking)}</p>
                  </div>

                  <div className="pb-booking-grid">
                    <div>
                      <span className="pb-muted">GUEST</span>
                      <strong>{getCustomer(booking)}</strong>
                      <small>
                        {booking.customerEmail ||
                          booking.guest?.email ||
                          booking.customer?.email ||
                          booking.customerPhone ||
                          booking.guest?.phone ||
                          booking.customer?.phone ||
                          "Contact details unavailable"}
                      </small>
                    </div>

                    <div>
                      <span className="pb-muted">CHECK-IN</span>
                      <strong>
                        {formatDate(booking.checkIn || booking.checkInDate)}
                      </strong>
                    </div>

                    <div>
                      <span className="pb-muted">CHECK-OUT</span>
                      <strong>
                        {formatDate(booking.checkOut || booking.checkOutDate)}
                      </strong>
                    </div>

                    <div>
                      <span className="pb-muted">ROOMS / GUESTS</span>
                      <strong>
                        {booking.rooms ?? booking.numberOfRooms ?? 1} room(s) ·{" "}
                        {booking.guests ??
                          booking.totalGuests ??
                          booking.adults ??
                          1}{" "}
                        guest(s)
                      </strong>
                    </div>
                  </div>

                  <div className="pb-booking-footer">
                    <div className="pb-total">
                      <span className="pb-muted">TOTAL AMOUNT</span>
                      <strong>
                        {money(
                          booking.pricing?.totalAmount ??
                            booking.totalAmount ??
                            booking.totalPrice ??
                            booking.amount
                        )}
                      </strong>
                      <small>
                        Payment:{" "}
                        {String(
                          booking.paymentStatus || "unknown"
                        ).replace(/_/g, " ")}
                      </small>
                    </div>

                    <div className="pb-actions">
                      <button
                        type="button"
                        className="pb-outline-btn"
                        onClick={() => openDetails(booking)}
                      >
                        View details
                      </button>

                      {canConfirm && (
                        <button
                          type="button"
                          className="pb-primary-btn"
                          disabled={isWorking}
                          onClick={() =>
                            updateBookingStatus(booking, "confirmed")
                          }
                        >
                          {isWorking ? "Updating..." : "✓ Confirm"}
                        </button>
                      )}

                      {canCancel && (
                        <button
                          type="button"
                          className="pb-cancel-btn"
                          disabled={isWorking}
                          onClick={() =>
                            updateBookingStatus(booking, "cancelled")
                          }
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {selectedBooking && (
        <div
          className="pb-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSelectedBooking(null);
          }}
        >
          <section
            className="pb-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pb-modal-title"
          >
            <div className="pb-modal-heading">
              <div>
                <span className="pb-eyebrow">RESERVATION DETAILS</span>
                <h2 id="pb-modal-title">Booking details</h2>
              </div>
              <button
                type="button"
                className="pb-modal-close"
                onClick={() => setSelectedBooking(null)}
                aria-label="Close details"
              >
                ×
              </button>
            </div>

            <div className="pb-detail-grid">
              <div>
                <span>Booking reference</span>
                <strong>
                  {selectedBooking.bookingReference ||
                    getId(selectedBooking) ||
                    "—"}
                </strong>
              </div>
              <div>
                <span>Guest</span>
                <strong>{getCustomer(selectedBooking)}</strong>
              </div>
              <div>
                <span>Palace</span>
                <strong>{getPalaceName(selectedBooking)}</strong>
              </div>
              <div>
                <span>Room category</span>
                <strong>{getRoomName(selectedBooking)}</strong>
              </div>
              <div>
                <span>Check-in</span>
                <strong>
                  {formatDate(
                    selectedBooking.checkIn || selectedBooking.checkInDate
                  )}
                </strong>
              </div>
              <div>
                <span>Check-out</span>
                <strong>
                  {formatDate(
                    selectedBooking.checkOut || selectedBooking.checkOutDate
                  )}
                </strong>
              </div>
              <div>
                <span>Payment method</span>
                <strong>{selectedBooking.paymentMethod || "—"}</strong>
              </div>
              <div>
                <span>Payment status</span>
                <strong>{selectedBooking.paymentStatus || "—"}</strong>
              </div>
              <div>
                <span>Total amount</span>
                <strong>
                  {money(
                    selectedBooking.pricing?.totalAmount ??
                      selectedBooking.totalAmount ??
                      selectedBooking.totalPrice
                  )}
                </strong>
              </div>
              <div>
                <span>Booking status</span>
                <strong>{getStatus(selectedBooking).replace(/_/g, " ")}</strong>
              </div>
            </div>

            <label className="pb-note-label" htmlFor="pb-vendor-note">
              Vendor note
            </label>
            <textarea
              id="pb-vendor-note"
              value={vendorNote}
              onChange={(e) => setVendorNote(e.target.value)}
              rows={3}
              placeholder="Add an internal note for this booking..."
            />

            <div className="pb-modal-actions">
              <button
                type="button"
                className="pb-outline-btn"
                onClick={() => setSelectedBooking(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="pb-primary-btn"
                disabled={actionId === getId(selectedBooking)}
                onClick={saveNote}
              >
                {actionId === getId(selectedBooking) ? "Saving..." : "Save note"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
